/**
 * Environment-aware logger utility that suppresses logs in production
 */

// Check if we're in production mode
// Using NODE_ENV which is standard and always available
const isProduction = process.env.NODE_ENV === "production";
const isDevelopment = process.env.NODE_ENV !== "production";

// Allow forcing debug mode via URL parameter or localStorage
const forceDebug =
  typeof window !== "undefined" &&
  (window.location?.search?.includes("debug=true") || localStorage?.getItem("debug") === "true");

// No-op function for suppressed logs
const noop = () => {};

export const logger = {
  log:
    isDevelopment || forceDebug
      ? (...args: any[]) => {
          console.log(...args);
        }
      : noop,
  error: (...args: any[]) => {
    console.error(...args);
  }, // Always keep errors
  warn:
    isDevelopment || forceDebug
      ? (...args: any[]) => {
          console.warn(...args);
        }
      : noop,
  info:
    isDevelopment || forceDebug
      ? (...args: any[]) => {
          console.info(...args);
        }
      : noop,
  debug:
    isDevelopment || forceDebug
      ? (...args: any[]) => {
          console.debug(...args);
        }
      : noop,
  dev:
    isDevelopment || forceDebug
      ? (...args: any[]) => {
          console.log("[DEV]", ...args);
        }
      : noop,
  table:
    isDevelopment || forceDebug
      ? (...args: any[]) => {
          console.table(...args);
        }
      : noop,
  time:
    isDevelopment || forceDebug
      ? (label: string) => {
          console.time(label);
        }
      : noop,
  timeEnd:
    isDevelopment || forceDebug
      ? (label: string) => {
          console.timeEnd(label);
        }
      : noop,
  group:
    isDevelopment || forceDebug
      ? (label?: string) => {
          console.group(label);
        }
      : noop,
  groupCollapsed:
    isDevelopment || forceDebug
      ? (label?: string) => {
          console.groupCollapsed(label);
        }
      : noop,
  groupEnd:
    isDevelopment || forceDebug
      ? () => {
          console.groupEnd();
        }
      : noop,
  clear:
    isDevelopment || forceDebug
      ? () => {
          console.clear();
        }
      : noop,
  count:
    isDevelopment || forceDebug
      ? (label?: string) => {
          console.count(label);
        }
      : noop,
  countReset:
    isDevelopment || forceDebug
      ? (label?: string) => {
          console.countReset(label);
        }
      : noop,
  assert:
    isDevelopment || forceDebug
      ? (condition?: boolean, ...data: any[]) => {
          console.assert(condition, ...data);
        }
      : noop,
  trace:
    isDevelopment || forceDebug
      ? (...args: any[]) => {
          console.trace(...args);
        }
      : noop,
  dir:
    isDevelopment || forceDebug
      ? (item: any, options?: any) => {
          console.dir(item, options);
        }
      : noop,
  dirxml:
    isDevelopment || forceDebug
      ? (...data: any[]) => {
          console.dirxml(...data);
        }
      : noop,
};

// Re-export original console for compatibility
export const originalConsole = console;

// Export functions to control logging
export const suppressConsoleLogs = () => {
  // Suppress Radix UI DialogContent accessibility warnings in development
  if (isDevelopment) {
    const originalError = console.error;
    console.error = (...args) => {
      if (
        typeof args[0] === "string" &&
        args[0].includes("DialogContent") &&
        args[0].includes("DialogTitle")
      ) {
        return; // Suppress this specific warning
      }
      originalError(...args);
    };
  }
};

export const restoreConsoleLogs = () => {};
export const setDevelopmentMode = () => {};
export const setProductionMode = () => {};

// Browser window functions for debugging
if (typeof window !== "undefined") {
  (window as any).enableLogs = () => {
    localStorage.setItem("debug", "true");
    window.location.reload();
  };

  (window as any).disableLogs = () => {
    localStorage.removeItem("debug");
    window.location.reload();
  };

  (window as any).enableDebug = () => {
    localStorage.setItem("debug", "true");
    console.log("Debug mode enabled. Logs will be visible after page reload.");
  };

  (window as any).loggerStatus = () => {
    return {
      mode: isProduction ? "production" : "development",
      logsEnabled: isDevelopment || forceDebug,
      forceDebug: forceDebug,
    };
  };
}

// Log current mode on initialization (only in development)
if (isDevelopment) {
  console.log(
    `[Logger] Running in ${isProduction ? "production" : "development"} mode. Logs are ${isDevelopment || forceDebug ? "enabled" : "disabled"}.`,
  );
}

export default logger;
